import { useMemo } from 'react';

import { serveInline3PQuerySuggestionsPhase1 } from '../experiments';

import { type QuerySuggestion } from './types';

const shuffleArray = <T>(arr: T[]): T[] => [...arr].sort(() => Math.random() - 0.5);

/**
 * Generic hook for building a product's query suggestions
 *
 * - 'static' treatment: 4 static suggestions
 * - 'dynamic' treatment without a query or with a query >4 words: 4 static suggestions
 * - 'dynamic' treatment with a query (≤4 words): 2 static + 2 dynamic suggestions, shuffled together
 *
 * @param staticSuggestionsPool - Pool of static query suggestions to sample from
 * @param dynamicSuggestionsPool - Pool of dynamic query suggestions to sample from
 * @param currentQuery - The current search query (used for dynamic suggestions)
 */
export const useProductSuggestions = (
	staticSuggestionsPool: string[],
	dynamicSuggestionsPool: Array<(query: string) => string>,
	currentQuery?: string,
): QuerySuggestion[] => {
	// Get treatment without firing exposure to use dynamic queries if needed
	const treatment = serveInline3PQuerySuggestionsPhase1({ fireExperimentExposure: false });

	return useMemo((): QuerySuggestion[] => {
		const staticSuggestions: QuerySuggestion[] = shuffleArray(staticSuggestionsPool)
			.slice(0, 4)
			.map((text) => ({ text, suggestionType: 'static' }));

		const currentQueryWordCount = currentQuery ? currentQuery.trim().split(/\s+/).length : 0;

		if (treatment !== 'dynamic' || !currentQuery || currentQueryWordCount > 4) {
			return staticSuggestions;
		}

		const dynamicSuggestions: QuerySuggestion[] = shuffleArray(dynamicSuggestionsPool)
			.slice(0, 2)
			.map((dynamicSuggestion) => ({
				text: dynamicSuggestion(currentQuery),
				suggestionType: 'dynamic',
			}));

		return shuffleArray([...staticSuggestions.slice(0, 2), ...dynamicSuggestions]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
};
